"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InputField } from "@/components/form/InputField";

export default function LoginPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const from = searchParams.get("from") || "/editor";
  const [loginMode, setLoginMode] = useState<"sms" | "password">("sms");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ phone?: string; code?: string; password?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // URL 参数自动登录：?phone=xxx&code=yyy → 绕过 codeSent 直接调登录 API
  useEffect(() => {
    const phoneParam = searchParams.get("phone")
    const codeParam = searchParams.get("code")
    if (phoneParam && codeParam && /^1[3-9]\d{9}$/.test(phoneParam) && codeParam.length === 6) {
      setPhone(phoneParam)
      setCode(codeParam)
      const autoLogin = async () => {
        setIsLoading(true)
        try {
          const res = await fetch("/api/auth/login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ phone: phoneParam, code: codeParam }),
          })
          const data = await res.json()
          if (data.success) {
            router.push(searchParams.get("from") || "/editor")
            router.refresh()
          }
        } finally {
          setIsLoading(false)
        }
      }
      autoLogin()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const validatePhone = () => {
    const newErrors: { phone?: string } = {};
    if (!phone) {
      newErrors.phone = "请输入手机号";
    } else if (!/^1[3-9]\d{9}$/.test(phone)) {
      newErrors.phone = "请输入有效的手机号";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateSmsForm = () => {
    const newErrors: { phone?: string; code?: string } = {};
    if (!phone) {
      newErrors.phone = "请输入手机号";
    } else if (!/^1[3-9]\d{9}$/.test(phone)) {
      newErrors.phone = "请输入有效的手机号";
    }
    if (!code) {
      newErrors.code = "请输入验证码";
    } else if (code.length !== 6) {
      newErrors.code = "验证码应为6位数字";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordForm = () => {
    const newErrors: { phone?: string; password?: string } = {};
    if (!phone) {
      newErrors.phone = "请输入手机号";
    } else if (!/^1[3-9]\d{9}$/.test(phone)) {
      newErrors.phone = "请输入有效的手机号";
    }
    if (!password) {
      newErrors.password = "请输入密码";
    } else if (password.length < 6) {
      newErrors.password = "密码至少6位";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendCode = async () => {
    if (!validatePhone()) return;

    setIsLoading(true);
    try {
      const response = await fetch("/api/auth/phone-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const data = await response.json();

      if (data.success) {
        setCodeSent(true);
        if (data.code) { setCode(data.code); console.log('[DEV] 验证码:', data.code); }
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setErrors({ phone: data.error || "发送验证码失败" });
      }
    } catch (error) {
      setErrors({ phone: "网络错误，请重试" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (loginMode === "sms") {
      if (!validateSmsForm()) return;
    } else {
      if (!validatePasswordForm()) return;
    }

    setIsLoading(true);
    try {
      const endpoint = loginMode === "sms" ? "/api/auth/login" : "/api/auth/login/password";
      const body = loginMode === "sms"
        ? JSON.stringify({ phone, code })
        : JSON.stringify({ phone, password });

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });
      const data = await response.json();

      if (data.success) {
        router.push(from);
        router.refresh();
      } else {
        const field = loginMode === "sms" ? "code" : "password";
        setErrors({ [field]: data.error || "登录失败" });
      }
    } catch (error) {
      const field = loginMode === "sms" ? "code" : "password";
      setErrors({ [field]: "网络错误，请重试" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-gray-100 dark:from-blue-950 dark:to-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">欢迎登录</CardTitle>
          <CardDescription>登录 D8D 云端 AI 开发平台</CardDescription>
        </CardHeader>
        <form onSubmit={handleLogin}>
          <CardContent className="space-y-4">
            <Tabs defaultValue="sms" onValueChange={(v) => { setLoginMode(v as "sms" | "password"); setErrors({}); }}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="sms">短信验证码登录</TabsTrigger>
                <TabsTrigger value="password">密码登录</TabsTrigger>
              </TabsList>

              <TabsContent value="sms" className="space-y-4 mt-4">
                <InputField
                  label="手机号"
                  name="phone"
                  type="tel"
                  placeholder="请输入手机号"
                  value={phone}
                  onChange={setPhone}
                  error={errors.phone}
                  required
                  disabled={isLoading}
                />

                <div className="flex gap-3">
                  <div className="flex-1">
                    <InputField
                      label="验证码"
                      name="code"
                      type="text"
                      placeholder="6位数字"
                      value={code}
                      onChange={setCode}
                      error={errors.code}
                      required
                      disabled={isLoading || !codeSent}
                      maxLength={6}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleSendCode}
                      disabled={isLoading || countdown > 0}
                      className="w-[120px]"
                    >
                      {countdown > 0 ? `${countdown}s后重发` : codeSent ? "重新发送" : "获取验证码"}
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="password" className="space-y-4 mt-4">
                <InputField
                  label="手机号"
                  name="phone"
                  type="tel"
                  placeholder="请输入手机号"
                  value={phone}
                  onChange={setPhone}
                  error={errors.phone}
                  required
                  disabled={isLoading}
                />
                <div>
                  <InputField
                    label="密码"
                    name="password"
                    type="password"
                    placeholder="请输入密码"
                    value={password}
                    onChange={setPassword}
                    error={errors.password}
                    required
                    disabled={isLoading}
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    <Link href="/forgot-password" className="text-primary hover:underline">
                      忘记密码？
                    </Link>
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
          <CardFooter className="flex-col gap-4">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "登录中..." : "登录"}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              还没有账号？{" "}
              <Link href="/register" className="text-primary hover:underline">
                立即注册
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
